import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { Button } from '../ui';
import { Select } from '../ui/Select';
import { SegmentedControl } from '../ui/SegmentedControl';
import { PencilIcon, SpinnerIcon, TrashIcon, UploadCloudIcon } from '../ui/Icons';
import {
  coerceSignatureValue,
  signatureAssetFromUpload,
  type SignatureAsset,
  type SignatureValue,
} from './types';

type Point = { x: number; y: number };
type CaptureMode = 'draw' | 'upload';

export type SignaturePadProps = {
  value?: unknown;
  onChange: (next: SignatureValue | null) => void;
  /** Persist an image file and return storage metadata (module-specific upload). */
  upload: (
    file: File,
    meta?: { source?: 'draw' | 'upload' },
  ) => Promise<SignatureAsset>;
  /** Resolve a storage key to an authenticated download path (relative to API base). */
  downloadPath: (storageKey: string) => string;
  readOnly?: boolean;
  disabled?: boolean;
  height?: number;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  /**
   * When true, fillers can place a stamp/seal as the canvas background
   * and sign (or upload a signature) on top of it — like stamping paper then signing.
   */
  allowStamp?: boolean;
  /** Accept attribute for uploaded signature/stamp images. */
  accept?: string;
  /** Vessels that have an uploaded stamp — shown as a dropdown beside Upload. */
  vesselStampOptions?: Array<{ value: number; label: string }>;
  /** Resolve a vessel stamp into a storage asset (e.g. copy into e-form attachments). */
  onSelectVesselStamp?: (vesselId: number) => Promise<SignatureAsset>;
};

async function fetchBlobUrl(downloadPath: string): Promise<{ url: string; revoke: () => void }> {
  const response = await api.get(downloadPath, { responseType: 'blob' });
  const raw = response.data as Blob;
  const ct =
    (response.headers['content-type'] as string | undefined) || raw.type || 'image/png';
  const blob = raw.type ? raw : new Blob([raw], { type: ct });
  const url = window.URL.createObjectURL(blob);
  return { url, revoke: () => window.URL.revokeObjectURL(url) };
}

function useStoragePreview(
  storageKey: string | undefined,
  downloadPath: (storageKey: string) => string,
) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let revoke: (() => void) | null = null;
    setPreviewUrl(null);
    if (!storageKey) return;
    // Local deferred previews (blob:/data:) — do not fetch via API.
    if (storageKey.startsWith('blob:') || storageKey.startsWith('data:')) {
      setPreviewUrl(storageKey);
      return;
    }
    void (async () => {
      try {
        const result = await fetchBlobUrl(downloadPath(storageKey));
        if (!active) {
          result.revoke();
          return;
        }
        revoke = result.revoke;
        setPreviewUrl(result.url);
      } catch {
        if (active) setPreviewUrl(null);
      }
    })();
    return () => {
      active = false;
      revoke?.();
    };
  }, [storageKey, downloadPath]);
  return previewUrl;
}

function canvasIsBlank(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const { width, height } = canvas;
  if (!width || !height) return true;
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) return false;
  }
  return true;
}

/** Single frame: stamp (back) + signature image (front), same as a stamped-then-signed paper. */
function CompositedFrame({
  stampUrl,
  signatureUrl,
  height,
  colors,
  emptyLabel,
  ringError,
}: {
  stampUrl: string | null;
  signatureUrl: string | null;
  height: number;
  colors: { border: string; surface: string; textMuted: string };
  emptyLabel?: string;
  ringError?: boolean;
}) {
  const hasStamp = Boolean(stampUrl);
  const hasSig = Boolean(signatureUrl);
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border bg-white dark:bg-slate-950',
        colors.border,
        ringError ? 'ring-2 ring-red-400/60' : '',
      )}
      style={{ height }}
    >
      {hasStamp ? (
        <img
          src={stampUrl!}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-contain p-3 opacity-90"
        />
      ) : null}
      {hasSig ? (
        <img
          src={signatureUrl!}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-contain p-3"
        />
      ) : null}
      {!hasStamp && !hasSig && emptyLabel ? (
        <div className={cn('flex h-full items-center justify-center text-xs', colors.textMuted)}>
          {emptyLabel}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Shared signature capture: draw pad and/or upload image, optional stamp behind the ink.
 * Modules supply upload + downloadPath adapters.
 */
export const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  upload,
  downloadPath,
  readOnly = false,
  disabled = false,
  height = 160,
  className,
  label,
  required,
  error,
  allowStamp = false,
  accept = 'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp',
  vesselStampOptions,
  onSelectVesselStamp,
}) => {
  const { colors } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<Point | null>(null);
  const [mode, setMode] = useState<CaptureMode>('draw');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [pendingStamp, setPendingStamp] = useState<SignatureAsset | null>(null);
  const [selectedVesselStampId, setSelectedVesselStampId] = useState<number | ''>('');
  const [localError, setLocalError] = useState<string | null>(null);
  const locked = readOnly || disabled || saving || uploadingStamp;

  const signature = coerceSignatureValue(value);
  const legacyText = typeof value === 'string' && value.trim() ? value.trim() : null;
  const effectiveStamp = signature?.stamp ?? pendingStamp ?? null;

  const signaturePreview = useStoragePreview(signature?.storageKey, downloadPath);
  const stampPreview = useStoragePreview(effectiveStamp?.storageKey, downloadPath);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDirty(false);
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const cssWidth = parent?.clientWidth || 480;
    const cssHeight = height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0f172a';
      ctx.lineWidth = 2.25;
    }
    setDirty(false);
  }, [height]);

  useEffect(() => {
    if (signature || locked || mode !== 'draw') return;
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [signature, locked, mode, resizeCanvas]);

  useEffect(() => {
    if (signature?.stamp) setPendingStamp(null);
  }, [signature?.stamp]);

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (locked) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    canvas.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = pointerPos(e);
    lastPoint.current = p;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
    setDirty(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || locked) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !lastPoint.current) return;
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    setDirty(true);
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPoint.current = null;
    try {
      canvasRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const commitSignature = async (
    asset: SignatureAsset,
    source: 'draw' | 'upload',
    stampOverride?: SignatureAsset | null,
  ) => {
    const stamp =
      stampOverride === undefined ? effectiveStamp ?? undefined : stampOverride ?? undefined;
    onChange({
      ...asset,
      source,
      signedAt: new Date().toISOString(),
      ...(stamp ? { stamp } : {}),
    });
    setPendingStamp(null);
    setDirty(false);
  };

  const handleClearAll = () => {
    if (locked) return;
    setLocalError(null);
    setPendingStamp(null);
    setSelectedVesselStampId('');
    onChange(null);
    clearCanvas();
  };

  const handleClearStamp = () => {
    if (locked) return;
    setLocalError(null);
    setSelectedVesselStampId('');
    if (signature) {
      const { stamp: _removed, ...rest } = signature;
      onChange({ ...rest });
    } else {
      setPendingStamp(null);
    }
  };

  const handleSaveDraw = async () => {
    if (locked) return;
    const canvas = canvasRef.current;
    if (!canvas || canvasIsBlank(canvas)) {
      setLocalError('Draw a signature first.');
      return;
    }
    setLocalError(null);
    setSaving(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png'),
      );
      if (!blob) throw new Error('Could not export signature image.');
      const file = new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' });
      const meta = await upload(file, { source: 'draw' });
      await commitSignature(signatureAssetFromUpload(meta), 'draw');
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not save signature.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSignatureFile = async (file: File | null) => {
    if (!file || locked) return;
    if (!file.type.startsWith('image/')) {
      setLocalError('Choose an image file (PNG, JPEG, or WebP).');
      return;
    }
    setLocalError(null);
    setSaving(true);
    try {
      const meta = await upload(file, { source: 'upload' });
      await commitSignature(signatureAssetFromUpload(meta), 'upload');
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not upload signature.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadStampFile = async (file: File | null) => {
    if (!file || locked) return;
    if (!file.type.startsWith('image/')) {
      setLocalError('Choose an image file for the stamp.');
      return;
    }
    setLocalError(null);
    setUploadingStamp(true);
    try {
      const meta = await upload(file);
      const asset = signatureAssetFromUpload(meta);
      setSelectedVesselStampId('');
      if (signature) {
        onChange({ ...signature, stamp: asset });
      } else {
        setPendingStamp(asset);
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not upload stamp.');
    } finally {
      setUploadingStamp(false);
    }
  };

  const handleSelectVesselStamp = async (vesselIdRaw: string) => {
    if (locked || !onSelectVesselStamp) return;
    const vesselId = Number(vesselIdRaw);
    if (!Number.isFinite(vesselId) || vesselId <= 0) {
      setSelectedVesselStampId('');
      return;
    }
    setLocalError(null);
    setUploadingStamp(true);
    setSelectedVesselStampId(vesselId);
    try {
      const asset = await onSelectVesselStamp(vesselId);
      if (signature) {
        onChange({ ...signature, stamp: asset });
      } else {
        setPendingStamp(asset);
      }
    } catch (e) {
      setSelectedVesselStampId('');
      setLocalError(e instanceof Error ? e.message : 'Could not load vessel stamp.');
    } finally {
      setUploadingStamp(false);
    }
  };

  const showCapture = !signature && !locked;
  const showSaved = Boolean(signature);
  const showLegacy = !signature && Boolean(legacyText) && locked;
  const vesselOptions = vesselStampOptions ?? [];
  const canPickVesselStamp = Boolean(onSelectVesselStamp) && vesselOptions.length > 0;

  const stampToolbar =
    allowStamp && !readOnly ? (
      <div
        className={cn(
          'space-y-2 rounded-lg border px-2.5 py-2',
          colors.border,
          colors.surface,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className={cn('text-xs font-medium', colors.text)}>Stamp / seal</p>
            <p className={cn('text-[11px]', colors.textMuted)}>
              {effectiveStamp
                ? 'Shown behind the signature on the pad'
                : canPickVesselStamp
                  ? 'Pick a vessel stamp or upload an image'
                  : 'Optional — sits under your signature like on paper'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {effectiveStamp ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!h-7"
                disabled={locked}
                onClick={handleClearStamp}
              >
                Remove
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="!h-7 gap-1"
              disabled={locked}
              onClick={() => stampInputRef.current?.click()}
            >
              {uploadingStamp ? (
                <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <UploadCloudIcon className="w-3.5 h-3.5" />
              )}
              {effectiveStamp ? 'Upload other' : 'Upload'}
            </Button>
          </div>
        </div>
        {canPickVesselStamp ? (
          <Select
            size="sm"
            value={selectedVesselStampId === '' ? '' : selectedVesselStampId}
            onChange={(e) => void handleSelectVesselStamp(String(e.target.value))}
            disabled={locked}
            placeholder="Select vessel stamp…"
            showClearButton={false}
            options={vesselOptions}
          />
        ) : null}
        <input
          ref={stampInputRef}
          type="file"
          accept={accept}
          className="hidden"
          disabled={locked}
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            e.target.value = '';
            void handleUploadStampFile(file);
          }}
        />
      </div>
    ) : null;

  return (
    <div className={cn('space-y-3', className)}>
      {label ? (
        <div className={cn('text-sm font-medium', colors.text)}>
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </div>
      ) : null}

      {stampToolbar}

      {showSaved ? (
        <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
          <div
            className={cn(
              'border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide',
              colors.border,
              colors.textMuted,
            )}
          >
            Signature
            {signature?.stamp ? ' · with stamp' : ''}
            {signature?.source === 'upload'
              ? ' · uploaded'
              : signature?.source === 'draw'
                ? ' · drawn'
                : ''}
          </div>
          <CompositedFrame
            stampUrl={stampPreview}
            signatureUrl={signaturePreview}
            height={height}
            colors={colors}
            emptyLabel="Loading…"
          />
          {!readOnly ? (
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2',
                colors.border,
              )}
            >
              <span className={cn('text-[11px]', colors.textMuted)}>
                {signature?.signedAt
                  ? `Saved ${new Date(signature.signedAt).toLocaleString()}`
                  : 'Signature saved'}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!h-7 gap-1"
                disabled={disabled || saving}
                onClick={handleClearAll}
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Clear
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {showLegacy ? (
        <div
          className={cn(
            'rounded-xl border border-dashed px-3 py-4 text-sm italic',
            colors.border,
            colors.textMuted,
          )}
        >
          Typed signature: {legacyText}
        </div>
      ) : null}

      {showCapture ? (
        <div className="space-y-2">
          <SegmentedControl
            aria-label="Signature capture method"
            size="sm"
            fullWidth
            value={mode}
            onChange={setMode}
            options={[
              {
                id: 'draw',
                label: 'Draw',
                icon: <PencilIcon className="w-3.5 h-3.5" />,
              },
              {
                id: 'upload',
                label: 'Upload',
                icon: <UploadCloudIcon className="w-3.5 h-3.5" />,
              },
            ]}
          />

          {mode === 'draw' ? (
            <>
              <div
                className={cn(
                  'relative overflow-hidden rounded-xl border bg-white dark:bg-slate-950',
                  colors.border,
                  error || localError ? 'ring-2 ring-red-400/60' : '',
                )}
                style={{ height }}
              >
                {stampPreview ? (
                  <img
                    src={stampPreview}
                    alt=""
                    className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain p-3 opacity-90"
                  />
                ) : null}
                <canvas
                  ref={canvasRef}
                  className={cn(
                    'relative z-10 block w-full touch-none bg-transparent',
                    locked ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair',
                  )}
                  style={{ height }}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={endStroke}
                  onPointerCancel={endStroke}
                  onPointerLeave={endStroke}
                />
                {!dirty ? (
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-0 z-20 flex items-center justify-center text-xs',
                      colors.textMuted,
                    )}
                  >
                    {stampPreview ? 'Sign over the stamp' : 'Draw signature here'}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!h-8"
                  disabled={locked || !dirty}
                  onClick={clearCanvas}
                >
                  Clear pad
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="!h-8 gap-1.5"
                  disabled={locked || !dirty}
                  onClick={() => void handleSaveDraw()}
                >
                  {saving ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : null}
                  {saving ? 'Saving…' : 'Save signature'}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <div
                className={cn(
                  'relative overflow-hidden rounded-xl border border-dashed',
                  colors.border,
                  error || localError ? 'ring-2 ring-red-400/60' : '',
                )}
                style={{ minHeight: height }}
              >
                {stampPreview ? (
                  <img
                    src={stampPreview}
                    alt=""
                    className="pointer-events-none absolute inset-0 h-full w-full object-contain p-3 opacity-80"
                  />
                ) : null}
                <div className="relative z-10 flex flex-col items-center justify-center gap-2 px-4 py-8 text-center">
                  <UploadCloudIcon
                    className={cn('h-8 w-8 opacity-60', colors.textMuted)}
                  />
                  <p className={cn('text-sm font-medium', colors.text)}>
                    {stampPreview
                      ? 'Upload signature to place over the stamp'
                      : 'Upload signature image'}
                  </p>
                  <p className={cn('text-xs', colors.textMuted)}>PNG, JPEG, or WebP</p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="!h-8 mt-2 gap-1.5"
                    disabled={locked}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    {saving ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : null}
                    {saving ? 'Uploading…' : 'Choose image'}
                  </Button>
                </div>
              </div>
              <input
                ref={uploadInputRef}
                type="file"
                accept={accept}
                className="hidden"
                disabled={locked}
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  void handleUploadSignatureFile(file);
                }}
              />
            </div>
          )}
        </div>
      ) : null}

      {locked && !signature && !legacyText ? (
        allowStamp && stampPreview ? (
          <CompositedFrame
            stampUrl={stampPreview}
            signatureUrl={null}
            height={height}
            colors={colors}
            emptyLabel="Stamp only — no signature"
          />
        ) : (
          <div
            className={cn(
              'rounded-xl border border-dashed px-3 py-8 text-center text-xs',
              colors.border,
              colors.textMuted,
            )}
          >
            No signature
          </div>
        )
      ) : null}

      {(error || localError) && (
        <p className="text-xs text-red-600">{error || localError}</p>
      )}
    </div>
  );
};

export default SignaturePad;
